import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, X, Send, Calculator, ArrowRight, Ruler, Home, Layers } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';
const COST_PER_SQFT = 1600; // Rs 1600 per sqft

// Floor options
const floorOptions = [
  { id: 'ground', label: 'Ground Floor', multiplier: 1 },
  { id: 'ground_1', label: 'Ground + 1 Floor', multiplier: 2 },
  { id: 'ground_2', label: 'Ground + 2 Floors', multiplier: 3 },
  { id: 'ground_3', label: 'Ground + 3 Floors', multiplier: 4 },
];

// Construction type
const constructionTypes = [
  { id: 'residential', label: 'Residential', icon: Home, description: 'Houses, Villas, Apartments' },
  { id: 'commercial', label: 'Commercial', icon: Building2, description: 'Shops, Offices, Warehouses' },
];

export default function ConstructionCalculator() {
  const [isOpen, setIsOpen] = useState(false);
  const [plotLength, setPlotLength] = useState('');
  const [plotWidth, setPlotWidth] = useState('');
  const [selectedFloors, setSelectedFloors] = useState(null);
  const [selectedType, setSelectedType] = useState('residential');
  const [showResult, setShowResult] = useState(false);

  const plotArea = plotLength && plotWidth ? Number(plotLength) * Number(plotWidth) : 0;
  const floorMultiplier = floorOptions.find(f => f.id === selectedFloors)?.multiplier || 1;
  const totalBuiltUpArea = plotArea * floorMultiplier;
  const estimatedCost = totalBuiltUpArea * COST_PER_SQFT;

  const canCalculate = plotLength && plotWidth && selectedFloors && plotArea > 0;

  const handleCalculate = () => {
    if (canCalculate) {
      setShowResult(true);
    }
  };

  const handleGetQuote = () => {
    const floorLabel = floorOptions.find(f => f.id === selectedFloors)?.label;
    const typeLabel = constructionTypes.find(t => t.id === selectedType)?.label;
    
    const message = encodeURIComponent(
      `*Construction Cost Quote Request*\n\n` +
      `*Construction Type:* ${typeLabel}\n` +
      `*Plot Size:* ${plotLength} x ${plotWidth} = ${plotArea} sq.ft\n` +
      `*Floors:* ${floorLabel}\n` +
      `*Total Built-up Area:* ${totalBuiltUpArea} sq.ft\n` +
      `*Estimated Cost:* ₹${estimatedCost.toLocaleString('en-IN')}\n` +
      `*(@ ₹${COST_PER_SQFT}/sq.ft)*\n\n` +
      `Please provide a detailed quote for my construction project.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const resetCalculator = () => {
    setPlotLength('');
    setPlotWidth('');
    setSelectedFloors(null);
    setSelectedType('residential');
    setShowResult(false);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    }
    if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} Lakhs`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <>
      {/* Calculator Trigger Button with Label */}
      <motion.div
        initial={{ scale: 0, x: 20 }}
        animate={{ scale: 1, x: 0 }}
        transition={{ delay: 1.3, type: 'spring', stiffness: 200 }}
        className="fixed bottom-[240px] right-6 z-50 flex items-center gap-2"
      >
        <motion.span 
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.8 }}
          className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 shadow-lg whitespace-nowrap"
        >
          Construction Calculator
        </motion.span>
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 bg-gradient-to-r from-orange-500 to-orange-700 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30 hover:scale-110 transition-transform"
          data-testid="construction-calc-btn"
        >
          <Building2 size={26} className="text-gray-900" />
        </button>
      </motion.div>

      {/* Calculator Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            data-testid="construction-calc-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg bg-[#ececec] border border-gray-200 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#ececec] border-b border-gray-200 p-6 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-700 rounded-xl flex items-center justify-center">
                      <Building2 size={24} className="text-gray-900" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl text-gray-900">Construction Calculator</h2>
                      <p className="text-gray-400 text-sm">Estimate your construction cost</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setIsOpen(false); resetCalculator(); }}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                    data-testid="construction-calc-close"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {!showResult ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-6"
                    >
                      {/* Construction Type */}
                      <div>
                        <label className="text-gray-900 text-sm font-medium mb-3 block">Construction Type</label>
                        <div className="grid grid-cols-2 gap-3">
                          {constructionTypes.map((type) => {
                            const Icon = type.icon;
                            return (
                              <button
                                key={type.id}
                                onClick={() => setSelectedType(type.id)}
                                className={`p-4 border rounded-xl transition-all text-center ${
                                  selectedType === type.id
                                    ? 'border-orange-500 bg-orange-500/10'
                                    : 'border-gray-200 hover:border-white/30'
                                }`}
                              >
                                <Icon size={28} className={`mx-auto ${selectedType === type.id ? 'text-orange-500' : 'text-gray-400'}`} />
                                <span className={`block mt-2 font-medium ${selectedType === type.id ? 'text-orange-500' : 'text-gray-900'}`}>
                                  {type.label}
                                </span>
                                <span className="text-gray-400 text-xs">{type.description}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Plot Dimensions */}
                      <div>
                        <label className="text-gray-900 text-sm font-medium mb-3 flex items-center gap-2">
                          <Ruler size={16} className="text-orange-500" />
                          Plot Dimensions (in feet)
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <input
                              type="number"
                              placeholder="Length"
                              value={plotLength}
                              onChange={(e) => setPlotLength(e.target.value)}
                              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                              data-testid="plot-length"
                            />
                            <span className="text-gray-400 text-xs mt-1 block">Length (ft)</span>
                          </div>
                          <div>
                            <input
                              type="number"
                              placeholder="Width"
                              value={plotWidth}
                              onChange={(e) => setPlotWidth(e.target.value)}
                              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                              data-testid="plot-width"
                            />
                            <span className="text-gray-400 text-xs mt-1 block">Width (ft)</span>
                          </div>
                        </div>
                        {plotArea > 0 && (
                          <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                            <span className="text-orange-400 text-sm">Plot Area: <strong>{plotArea} sq.ft</strong></span>
                          </div>
                        )}
                      </div>

                      {/* Floor Selection */}
                      <div>
                        <label className="text-gray-900 text-sm font-medium mb-3 flex items-center gap-2">
                          <Layers size={16} className="text-orange-500" />
                          Number of Floors
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {floorOptions.map((floor) => (
                            <button
                              key={floor.id}
                              onClick={() => setSelectedFloors(floor.id)}
                              className={`p-3 border rounded-xl transition-all ${
                                selectedFloors === floor.id
                                  ? 'border-orange-500 bg-orange-500/10'
                                  : 'border-gray-200 hover:border-white/30'
                              }`}
                            >
                              <span className={`block font-medium ${selectedFloors === floor.id ? 'text-orange-500' : 'text-gray-900'}`}>
                                {floor.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Rate Info */}
                      <div className="p-4 bg-white rounded-xl border border-gray-200">
                        <p className="text-gray-400 text-sm">
                          Current construction rate: <span className="text-orange-500 font-bold">₹{COST_PER_SQFT}/sq.ft</span>
                        </p>
                      </div>

                      {/* Calculate Button */}
                      <button
                        onClick={handleCalculate}
                        disabled={!canCalculate}
                        className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                          canCalculate
                            ? 'bg-gradient-to-r from-orange-500 to-orange-700 text-gray-900 hover:opacity-90'
                            : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        }`}
                        data-testid="calc-estimate-btn"
                      >
                        <Calculator size={20} />
                        Calculate Estimate
                        <ArrowRight size={18} />
                      </button>
                    </motion.div>
                  ) : (
                    /* Result Screen */
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center"
                    >
                      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-orange-500 to-orange-700 rounded-full flex items-center justify-center">
                        <Building2 size={36} className="text-gray-900" />
                      </div>
                      <h3 className="text-xl text-gray-900 mb-2">Estimated Construction Cost</h3>
                      <p className="text-gray-400 text-sm mb-6">Based on your inputs</p>
                      
                      <div className="bg-white p-6 rounded-xl mb-6">
                        <span className="font-heading text-5xl bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                          {formatPrice(estimatedCost)}
                        </span>
                        <p className="text-gray-400 text-sm mt-2">*Prices may vary based on design & materials</p>
                      </div>

                      {/* Summary */}
                      <div className="text-left bg-white/50 p-4 rounded-xl mb-6 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Construction Type:</span>
                          <span className="text-gray-900">{constructionTypes.find(t => t.id === selectedType)?.label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Plot Size:</span>
                          <span className="text-gray-900">{plotLength} x {plotWidth} = {plotArea} sq.ft</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Floors:</span>
                          <span className="text-gray-900">{floorOptions.find(f => f.id === selectedFloors)?.label}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Total Built-up Area:</span>
                          <span className="text-gray-900 font-bold">{totalBuiltUpArea} sq.ft</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                          <span className="text-gray-400">Rate:</span>
                          <span className="text-orange-500 font-bold">₹{COST_PER_SQFT}/sq.ft</span>
                        </div>
                      </div>

                      <button
                        onClick={handleGetQuote}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-700 text-gray-900 font-semibold py-4 rounded-xl flex items-center justify-center gap-2 mb-3 hover:opacity-90 transition-opacity"
                        data-testid="construction-quote-btn"
                      >
                        <Send size={18} />
                        Get Detailed Quote on WhatsApp
                      </button>
                      <button
                        onClick={resetCalculator}
                        className="w-full btn-secondary"
                        data-testid="construction-recalc-btn"
                      >
                        Recalculate
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
